/**
 * EnhancedSectionRegistry - Plugin-based section registration system
 * Provides automatic registration, lazy loading, chakra integration, and state persistence
 */

import { devLog, devWarn } from '../../../../../utils/devLog';
import React from 'react';
import { JatakaResponse } from '../../../../../models/api';
import { AccordionDetail } from '../types';
import { chakraEventAPI, ChakraEventConfig } from '../api/ChakraEventAPI';
import { lazyCalculator } from '../lazy/LazyTableCalculator';

export interface EnhancedSectionConfig {
    id: string;
    parentSection: string;
    label: string;
    component: React.ComponentType<any>;
    type: 'table' | 'toggle' | 'default';
    order?: number;

    // Lazy loading configuration
    lazy?: {
        calculator: string; // ID of registered calculator
        loader?: React.ComponentType<{ progress?: number }>; // Loading component
        dependencies?: string[]; // Additional dependencies beyond calculator
    };

    // Chakra integration
    chakra?: {
        id: string; // Chakra ID for binding
        triggerOn: 'show' | 'expand' | 'click';
        autoRegister?: boolean; // Auto-register chakra if not exists
    };

    // State persistence configuration
    persistence?: {
        enabled: boolean;
        stateKeys: string[]; // Which state parts to save
        namespace?: string; // Custom namespace for storage
    };

    // Event handlers
    events?: {
        onMount?: (apiResponse: JatakaResponse) => void;
        onExpand?: (isExpanded: boolean, apiResponse?: JatakaResponse) => void;
        onCalculationStart?: (calculatorId: string) => void;
        onCalculationComplete?: (result: any, calculatorId: string) => void;
        onCalculationError?: (error: Error, calculatorId: string) => void;
        onChakraToggle?: (enabled: boolean, chakraId: string) => void;
    };

    // Conditional rendering
    condition?: (apiResponse: JatakaResponse) => boolean;

    // Component props override
    componentProps?: Record<string, any>;
}

export interface SectionRenderContext {
    apiResponse: JatakaResponse;
    isExpanded: boolean;
    onToggleExpand: (expanded: boolean) => void;
    sectionState: any; // From SectionStateManager
}

/**
 * Main EnhancedSectionRegistry class
 */
class EnhancedSectionRegistry {
    private sections = new Map<string, EnhancedSectionConfig>();
    private lazyComponents = new Map<string, React.ComponentType<any>>();

    /**
     * Register a section with full configuration
     */
    register(config: EnhancedSectionConfig): void {
        // Validate configuration
        this.validateConfig(config);

        // Auto-register chakra if specified
        if (config.chakra?.autoRegister !== false && config.chakra) {
            this.autoRegisterChakra(config);
        }

        // Auto-register lazy calculator if specified
        if (config.lazy?.calculator) {
            this.validateLazyCalculator(config.lazy.calculator);
        }

        // Store configuration
        this.sections.set(config.id, config);

        // Minimal logging for development only
        if (process.env.NODE_ENV === 'development') {
            const features = [];
            if (config.lazy) features.push('lazy');
            if (config.chakra) features.push('chakra');
            if (config.persistence?.enabled) features.push('persistence');

        }
    }

    /**
     * Get sections by parent section, filtered and sorted
     */
    getSectionsByParent(
        parentSection: string,
        apiResponse?: JatakaResponse
    ): EnhancedSectionConfig[] {
        return Array.from(this.sections.values())
            .filter(section => {
                // Parent section match
                if (section.parentSection !== parentSection) return false;

                // Conditional rendering
                if (section.condition && apiResponse) {
                    return section.condition(apiResponse);
                }

                return true;
            })
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    /**
     * Generate accordion detail fields for a parent section
     */
    generateDetailFields(
        parentSection: string,
        apiResponse: JatakaResponse
    ): AccordionDetail[] {
        const sections = this.getSectionsByParent(parentSection, apiResponse);

        return sections.map(section => ({
            label: section.label,
            value: '',
            type: section.type,
            category: 'detailed' as const,
            parentSection: section.parentSection,
            // Add metadata for rendering
            _sectionId: section.id,
            _config: section
        } as AccordionDetail & { _sectionId: string; _config: EnhancedSectionConfig }));
    }

    /**
     * Render a section component with all enhancements
     */
    renderSection(
        sectionId: string,
        context: SectionRenderContext
    ): React.ReactElement | null {
        const config = this.sections.get(sectionId);
        if (!config) {
            devWarn(`⚠️ EnhancedSectionRegistry: Section "${sectionId}" not found`);
            return null;
        }

        // Check condition
        if (config.condition && !config.condition(context.apiResponse)) {
            return null;
        }

        // Handle lazy loading
        if (config.lazy) {
            return this.renderLazySection(config, context);
        }

        // Handle chakra integration
        if (config.chakra) {
            return this.renderChakraSection(config, context);
        }

        // Regular section rendering
        return this.renderRegularSection(config, context);
    }

    /**
     * Render lazy-loaded section with progress
     */
    private renderLazySection(
        config: EnhancedSectionConfig,
        context: SectionRenderContext
    ): React.ReactElement {
        const LazyWrapper = React.lazy(async () => {
            try {
                // Fire onCalculationStart event
                if (config.events?.onCalculationStart) {
                    config.events.onCalculationStart(config.lazy!.calculator);
                }

                // Perform lazy calculation
                const result = await lazyCalculator.calculate(
                    config.lazy!.calculator,
                    context.apiResponse,
                    (progress) => {
                        // Progress updates could be handled here
                    }
                );

                // Fire onCalculationComplete event
                if (config.events?.onCalculationComplete) {
                    config.events.onCalculationComplete(result, config.lazy!.calculator);
                }

                // Return component with calculated data
                return {
                    default: (props: any) => React.createElement(config.component, {
                        ...props,
                        ...config.componentProps,
                        calculatedData: result,
                        apiResponse: context.apiResponse
                    })
                };

            } catch (error) {
                // Fire onCalculationError event
                if (config.events?.onCalculationError) {
                    config.events.onCalculationError(error as Error, config.lazy!.calculator);
                }

                // Return error component
                return {
                    default: () => React.createElement('div', {
                        style: { color: 'red', padding: '16px' }
                    }, `Error loading ${config.label}: ${error}`)
                };
            }
        });

        // Wrap with Suspense and optional loader
        const LoaderComponent = config.lazy?.loader || DefaultLoader;

        return React.createElement(
            React.Suspense,
            { fallback: React.createElement(LoaderComponent, { progress: 0 }) },
            React.createElement(LazyWrapper)
        );
    }

    /**
     * Render section with chakra integration
     */
    private renderChakraSection(
        config: EnhancedSectionConfig,
        context: SectionRenderContext
    ): React.ReactElement {
        // This would integrate with chakra visibility logic
        // For now, render the regular component
        return this.renderRegularSection(config, context);
    }

    /**
     * Render regular section
     */
    private renderRegularSection(
        config: EnhancedSectionConfig,
        context: SectionRenderContext
    ): React.ReactElement {
        const props = {
            ...config.componentProps,
            apiResponse: context.apiResponse,
            isExpanded: context.isExpanded,
            onToggleExpand: context.onToggleExpand
        };

        return React.createElement(config.component, props);
    }

    /**
     * Auto-register chakra for section
     */
    private autoRegisterChakra(config: EnhancedSectionConfig): void {
        if (!config.chakra) return;

        const chakraConfig: ChakraEventConfig = {
            id: config.chakra.id,
            type: 'custom',
            label: `${config.label} Chakra`,
            storageKey: `chakra:${config.chakra.id}`,
            defaultValue: false
        };

        try {
            chakraEventAPI.registerChakra(chakraConfig);
        } catch (error) {
            devWarn(`⚠️ EnhancedSectionRegistry: Failed to auto-register chakra for "${config.id}":`, error);
        }
    }

    /**
     * Validate lazy calculator exists
     */
    private validateLazyCalculator(calculatorId: string): void {
        // This would check if calculator is registered
        // Minimal logging for development only
        if (process.env.NODE_ENV === 'development') {
        }
    }

    /**
     * Validate section configuration
     */
    private validateConfig(config: EnhancedSectionConfig): void {
        if (!config.id) throw new Error('Section id is required');
        if (!config.parentSection) throw new Error('Section parentSection is required');
        if (!config.label) throw new Error('Section label is required');
        if (!config.component) throw new Error('Section component is required');
        if (!['table', 'toggle', 'default'].includes(config.type)) {
            throw new Error('Section type must be table, toggle, or default');
        }

        // Check for duplicate IDs
        if (this.sections.has(config.id)) {
            devWarn(`⚠️ EnhancedSectionRegistry: Overwriting existing section "${config.id}"`);
        }
    }

    /**
     * Unregister a section
     */
    unregister(sectionId: string): void {
        if (this.sections.delete(sectionId)) {
            devLog(`🗑️ EnhancedSectionRegistry: Unregistered section "${sectionId}"`);
        }
    }

    /**
     * Get all registered sections
     */
    getAllSections(): EnhancedSectionConfig[] {
        return Array.from(this.sections.values());
    }

    /**
     * Get section by ID
     */
    getSection(sectionId: string): EnhancedSectionConfig | undefined {
        return this.sections.get(sectionId);
    }

    /**
     * Get sections by parent (alias for getSectionsByParent)
     */
    getByParent(parentSection: string, apiResponse?: JatakaResponse): EnhancedSectionConfig[] {
        return this.getSectionsByParent(parentSection, apiResponse);
    }

    /**
     * Clear all registrations
     */
    clear(): void {
        this.sections.clear();
        this.lazyComponents.clear();
        devLog(`🗑️ EnhancedSectionRegistry: Cleared all registrations`);
    }

    /**
     * Get debug information
     */
    getDebugInfo(): Record<string, any> {
        const sections = Array.from(this.sections.values());

        return {
            totalSections: sections.length,
            sectionsByParent: this.groupSectionsByParent(sections),
            sectionsWithLazy: sections.filter(s => s.lazy).length,
            sectionsWithChakra: sections.filter(s => s.chakra).length,
            sectionsWithPersistence: sections.filter(s => s.persistence?.enabled).length,
            sectionsWithEvents: sections.filter(s => s.events && Object.keys(s.events).length > 0).length
        };
    }

    /**
     * Group sections by parent for debugging
     */
    private groupSectionsByParent(sections: EnhancedSectionConfig[]): Record<string, string[]> {
        const grouped: Record<string, string[]> = {};

        sections.forEach(section => {
            if (!grouped[section.parentSection]) {
                grouped[section.parentSection] = [];
            }
            grouped[section.parentSection].push(section.id);
        });

        return grouped;
    }
}

/**
 * Default loader component
 */
const DefaultLoader: React.FC<{ progress?: number }> = ({ progress = 0 }) => {
    return React.createElement('div', {
        style: {
            padding: '16px',
            textAlign: 'center',
            color: '#666',
            fontSize: '14px'
        }
    }, `Loading... ${progress > 0 ? `${Math.round(progress)}%` : ''}`);
};

// Singleton instance
export const enhancedRegistry = new EnhancedSectionRegistry();

export default enhancedRegistry;